import {
  CheckCircleOutlined,
  DoNotDisturbOnOutlined,
  ErrorOutlineOutlined,
  KeyboardArrowUpOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { Collapse, Divider, Paper, Stack, styled, Typography } from '@mui/material';
import { PropsWithChildren, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { UploadedFileList } from '~/components/FileUpload/UploadedFileList/UploadedFileList';

enum ComplianceCheckResult {
  NOT_APPLICABLE = 'not_applicable',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}
export type ComplianceCheckResultType = `${ComplianceCheckResult}`;

const iconMap: Record<ComplianceCheckResult, JSX.Element> = {
  [ComplianceCheckResult.NOT_APPLICABLE]: <DoNotDisturbOnOutlined color="secondary" fontSize="small" />,
  [ComplianceCheckResult.SUCCESS]: <CheckCircleOutlined color="success" fontSize="small" />,
  [ComplianceCheckResult.WARNING]: <WarningAmberOutlined color="warning" fontSize="small" />,
  [ComplianceCheckResult.ERROR]: <ErrorOutlineOutlined color="error" fontSize="small" />,
};

const I18N_NAMESPACE = I18nNamespace.conversation;
const KEY_PREFIX = 'complianceCheck';

interface ComplianceCheckProps {
  title: string;
  result: ComplianceCheckResultType;
  additionalFiles?: string[];
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
}));

const StyledTypography = styled(Typography)({
  flexGrow: 1,
  minWidth: 0,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
});

const StyledExpandIcon = styled(KeyboardArrowUpOutlined, { shouldForwardProp: (prop) => prop !== 'checked' })<{
  checked: boolean;
}>(({ theme, checked }) => ({
  color: theme.palette.text.secondary,
  transform: checked ? 'rotate(0deg)' : 'rotate(-180deg)',
  transition: `transform ${TRANSITION_TIMEOUT}ms ease-in-out`,
}));

const TRANSITION_TIMEOUT = 200;

export const ComplianceCheck = ({
  title,
  result,
  children,
  additionalFiles,
}: PropsWithChildren<ComplianceCheckProps>) => {
  const { t, i18n } = useTranslation(I18N_NAMESPACE, { keyPrefix: KEY_PREFIX });

  // Using frontend based translation if existing. Otherwise falling back to the provided details by LLM.
  const hasTranslation = i18n.exists(`${KEY_PREFIX}.${title}`, { ns: I18N_NAMESPACE });
  const displayTitle = hasTranslation ? t(title) : title;

  const [checked, setChecked] = useState<boolean>(false);
  const toggleChecked = () => setChecked((prev) => !prev);

  const icon = iconMap[result];
  return (
    <StyledPaper>
      <Stack direction="row" onClick={toggleChecked} gap={2} alignItems="center">
        {icon}
        <StyledTypography variant="subtitle2" color="textSecondary" sx={{}}>
          {displayTitle}
        </StyledTypography>
        <StyledExpandIcon checked={checked} fontSize="small" />
      </Stack>
      <Collapse in={checked} timeout={TRANSITION_TIMEOUT}>
        <Typography sx={{ marginTop: 2 }} variant="body2" color="textSecondary">
          {children}
        </Typography>
        {additionalFiles && additionalFiles.length > 0 && (
          <>
            <Divider sx={{ marginY: 2 }} />
            <UploadedFileList files={additionalFiles.map((fileId) => ({ fileId }))} />
          </>
        )}
      </Collapse>
    </StyledPaper>
  );
};
